import { useState } from "react";
import { useNewsletterApi, useRecentlySubscribedNewsletters } from "scmp-sdk";
import { useSdkContext } from "scmp-sdk/components/contexts";
import { useNewsletterContext } from "scmp-sdk/components/newsletter/newsletter-widget/contexts";
import { Variant } from "scmp-sdk/components/newsletter/newsletter-widget/enums";

import { useAccountsApi, useCookieValue } from "shared/hooks";
import { notEmpty } from "shared/lib/utils";

import { CombinedSocialAndEmail } from "./combined-social-and-email";
import { EmailForm } from "./email-form";
import { ActionComponent } from "./enums";
import { getTriggerPoint } from "./helpers";
import { SocialButtons } from "./social-buttons";
import { SocialType } from "./social-buttons/enums";
import { StyledSignUpButton } from "./styles";
import type { ApiResponse } from "./types";

export const useSignUpNewsletter = () => {
  const { campaignName, dataMatrix, newsletterItems, packageDisplayName, variant } =
    useNewsletterContext();
  const { config, handleTrackGA4, handleTrackGTM, handleTrackMirror, user } = useSdkContext();
  const { addRecentlySubscribed } = useRecentlySubscribedNewsletters();
  const { handleSubscribeNewsletter } = useNewsletterApi();
  const { createUser } = useAccountsApi(config.accounts.host);
  const [loginType] = useCookieValue("scmp_user_login_type");
  const isLoggedIn = notEmpty(user?.useremail);

  const handleSubmit = async (email: string): Promise<ApiResponse> => {
    if (newsletterItems?.length === 0) return { error: true };
    const response = await handleSubscribeNewsletter(
      newsletterItems.map(newsletterItem => newsletterItem.entityId),
      email,
      dataMatrix,
    );

    if (response.errors?.length) return { error: true };

    if (!isLoggedIn) {
      addRecentlySubscribed(newsletterItems.map(item => item.entityId));
    }

    if (packageDisplayName) {
      handleTrackGA4({
        action: "sys",
        category: "nl",
        customized_parameters: {
          internal_campaign: campaignName ?? "",
          method: isLoggedIn ? (loginType?.toLowerCase() ?? "") : "email",
          newsletter_name: newsletterItems.map(item => item.name).join("|"),
          trigger_point: getTriggerPoint(variant),
          ...(packageDisplayName && { package_name: packageDisplayName }),
        },
        subcategory: "sign_up",
      });
      handleTrackMirror({
        event_name: "newsletter_sign_up",
        newsletter_subscribe: newsletterItems.map(item => item.name).join("|"),
      });
      handleTrackGTM({
        event: "newsletter-subscribe",
        "newsletter-id": newsletterItems.map(item => item.entityId).join("|"),
        "newsletter-name": newsletterItems.map(item => item.name).join("|"),
      });
      return {
        error: false,
        result: {
          uid: response.entity?.uid?.entity?.uid ?? "",
        },
      };
    }

    newsletterItems.forEach((newsletter, index) => {
      const handleCustomTrackGA = () => {
        // Acquisition popup variant will send one tracking event
        if (variant === Variant.AcquisitionNewsletterPopup) {
          if (index > 0 || !campaignName) return;
          handleTrackGA4({
            action: "sys",
            category: "nl",
            customized_parameters: {
              internal_campaign: campaignName ?? "",
              method: isLoggedIn ? (loginType?.toLowerCase() ?? "") : "email",
              newsletter_name: newsletter.name,
              trigger_point: "ppto",
            },
            subcategory: "sign_up",
          });

          return;
        }
        handleTrackGA4({
          action: "sys",
          category: "nl",
          customized_parameters: {
            internal_campaign: campaignName ?? "",
            method: isLoggedIn ? (loginType?.toLowerCase() ?? "") : "email",
            newsletter_name: newsletter.name,
            trigger_point: getTriggerPoint(variant),
          },
          subcategory: "sign_up",
        });
      };

      handleCustomTrackGA();
      handleTrackMirror({
        event_name: "newsletter_sign_up",
        newsletter_subscribe: newsletter.name,
      });
      handleTrackGTM({
        event: "newsletter-subscribe",
        "newsletter-id": newsletter.entityId,
        "newsletter-name": newsletter.name,
      });
    });

    return {
      error: false,
      result: {
        uid: response.entity?.uid?.entity?.uid ?? "",
      },
    };
  };

  const handleSubmitWithEmail = async (
    email: string,
    recaptchaToken: string,
    isAgreedReceiveMarketing: boolean,
  ): Promise<ApiResponse> => {
    if (!newsletterItems || newsletterItems.length === 0) {
      console.warn("Newsletter items not available for email submission");
      return { error: true };
    }

    try {
      const response = await handleSubmit(email);
      if (response.error) return response;
      else if (!response.result?.uid) {
        await createUser({
          newsletterId: JSON.stringify(newsletterItems.map(item => item.entityId)),
          newsletterName: JSON.stringify(newsletterItems.map(item => item.name)),
          optIn: isAgreedReceiveMarketing ? 1 : 0,
          recaptchaToken,
          recaptchaVersion: "v3",
          username: email,
        });

        handleTrackGA4({
          action: "sys",
          category: "login",
          customized_parameters: {
            internal_campaign: campaignName ?? "",
            method: "email",
            newsletter_name: newsletterItems.map(item => item.name).join("|"),
            ...(packageDisplayName && { package_name: packageDisplayName }),
            paywall_type: "newsletter_paywall",
            result: "register_success",
            trigger_point: "ppto",
          },
          subcategory: "result",
        });
      }
    } catch {
      return {
        error: true,
      };
    }
    return {
      error: false,
    };
  };

  return {
    handleSubmit,
    handleSubmitWithEmail,
  };
};

export const useAction = ({
  initialActionComponent,
  isAgreedReceiveMarketing,
  isDisableSignupButtonMouseOver,
  isShowOverlaySignUp,
  onSuccess,
}: {
  initialActionComponent?: ActionComponent;
  isAgreedReceiveMarketing: boolean;
  isDisableSignupButtonMouseOver: boolean;
  isShowOverlaySignUp?: boolean;
  onSuccess: () => void;
}) => {
  const { handleTrackGA4, user } = useSdkContext();
  const {
    campaignName,
    newsletterItemForDisplay,
    newsletterItems,
    packageDisplayName,
    setLastSubscribedNewsletter,
    signUpButtonDisplayComponent,
    variant,
  } = useNewsletterContext();
  const isLoggedIn = notEmpty(user?.useremail);
  const [loginType] = useCookieValue("scmp_user_login_type");
  const [actionComponent, setActionComponent] = useState(() => {
    if (isLoggedIn) return ActionComponent.SignUpButton;
    else if (isShowOverlaySignUp) return ActionComponent.OverlaySignUpButton;
    return ActionComponent.SocialButtons;
  });

  const [isLoading, setIsLoading] = useState(false);
  const handleSignUpClick = async () => {
    if (!newsletterItems || newsletterItems.length === 0) {
      console.warn("Newsletter items not available yet");
      return;
    }

    const handleTracking = () => {
      if (variant !== Variant.AcquisitionNewsletterPopup || !campaignName) {
        handleTrackGA4({
          action: "click",
          category: "nl",
          customized_parameters: {
            method: isLoggedIn ? (loginType?.toLowerCase() ?? "") : "email",
            newsletter_name: newsletterItems.map(item => item.name).join("|") ?? "",
            trigger_point: getTriggerPoint(variant),
            ...(packageDisplayName && { package_name: packageDisplayName }),
          },
          subcategory: "sign_up",
        });
        return;
      }

      handleTrackGA4({
        action: "click",
        category: "promopopup",
        customized_parameters: {
          cta_type: "nl_signup",
          internal_campaign: campaignName,
          message: signUpButtonDisplayComponent?.toString() ?? "SIGN UP NOW", // eslint-disable-line @typescript-eslint/no-base-to-string
          step: "1",
        },
        subcategory: "",
      });
    };

    handleTracking();
    if (actionComponent === ActionComponent.OverlaySignUpButton) {
      if (initialActionComponent === ActionComponent.CombinedSocialAndEmail) {
        setActionComponent(ActionComponent.CombinedSocialAndEmail);
      } else {
        setActionComponent(ActionComponent.SocialButtons);
      }
    } else if (isLoggedIn && user?.useremail) {
      setIsLoading(true);
      const { error } = await handleSubmit(user.useremail);
      if (newsletterItemForDisplay) setLastSubscribedNewsletter?.(newsletterItemForDisplay.name);
      if (!error) onSuccess();
      setIsLoading(false);
    }
  };

  const handleSignUpMouseover = () => {
    if (isLoggedIn) return;
    if (initialActionComponent === ActionComponent.CombinedSocialAndEmail) {
      setActionComponent(ActionComponent.CombinedSocialAndEmail);
      return;
    }
    if (isDisableSignupButtonMouseOver || actionComponent !== ActionComponent.OverlaySignUpButton)
      return;
    setActionComponent(ActionComponent.SocialButtons);
  };

  const { handleSubmit, handleSubmitWithEmail } = useSignUpNewsletter();
  const handleRenderActionComponent = () => {
    switch (actionComponent) {
      case ActionComponent.CombinedSocialAndEmail:
        return (
          <CombinedSocialAndEmail
            isAgreedReceiveMarketing={isAgreedReceiveMarketing}
            onSubmit={async ({ email, recaptchaToken }) => {
              handleTrackGA4({
                action: "click",
                category: "nl",
                customized_parameters: {
                  method: SocialType.Email,
                  newsletter_name: newsletterItems.map(item => item.name).join("|") ?? "",
                  trigger_point: getTriggerPoint(variant),
                  ...(packageDisplayName && { package_name: packageDisplayName }),
                },
                subcategory: "sign_up",
              });
              const response = await handleSubmitWithEmail(
                email,
                recaptchaToken,
                isAgreedReceiveMarketing,
              );
              if (!response.error) onSuccess();
              return response;
            }}
          />
        );
      case ActionComponent.EmailForm:
        return (
          <EmailForm
            onPreviousClick={() => setActionComponent(ActionComponent.SocialButtons)}
            onSubmit={async ({ email, recaptchaToken }) => {
              handleTrackGA4({
                action: "click",
                category: "nl",
                customized_parameters: {
                  method: SocialType.Email,
                  newsletter_name: newsletterItems.map(item => item.name).join("|") ?? "",
                  trigger_point: getTriggerPoint(variant),
                  ...(packageDisplayName && { package_name: packageDisplayName }),
                },
                subcategory: "sign_up",
              });
              const response = await handleSubmitWithEmail(
                email,
                recaptchaToken,
                isAgreedReceiveMarketing,
              );
              if (!response.error) onSuccess();
              return response;
            }}
          />
        );
      case ActionComponent.SocialButtons:
        return (
          <SocialButtons
            isAgreedReceiveMarketing={isAgreedReceiveMarketing}
            onEmailClick={() => {
              setActionComponent(ActionComponent.EmailForm);
            }}
          />
        );
      default:
        return (
          <StyledSignUpButton
            isLoading={isLoading}
            onClick={() => void handleSignUpClick()}
            onMouseOver={handleSignUpMouseover}
            size="large"
          >
            {signUpButtonDisplayComponent ?? "SIGN UP NOW"}
          </StyledSignUpButton>
        );
    }
  };

  return {
    actionComponent,
    handleRenderActionComponent,
  };
};
